<template>
  <div class="p-3 vstack gap-3 bg-light">
    <template v-if="resultOrchestrators.length > 0">
      <div v-if="resultOrchestrators.length != 1" class="p-3 bg-white rounded shadow-sm">
        <h3>{{ t("backend.e2e.voting_round_reports.show.overall_turnout") }}</h3>
        <div class="accent-line mt-2 mb-4"></div>
        <!-- turnout.voted reflects digital ballots only; rounds resolved entirely via
             external tallies legitimately finish with turnout.voted === 0. -->
        <TurnoutTable
          v-if="votingRoundReport.status === 'finished'"
          :turnout="votingRoundReport.turnout"
        />
        <template v-else>
          {{ t("backend.e2e.voting_round_reports.show.waiting_for_results") }}
        </template>
      </div>

      <div v-if="contestsToMap" class="p-3 bg-white rounded shadow-sm">
        <h3>{{ t("backend.e2e.voting_round_reports.show.write_in_mapping") }}</h3>
        <div class="accent-line mt-2 mb-4"></div>
        <p>{{ t("backend.e2e.voting_round_reports.show.write_in_mapping_description") }}</p>
        <button v-if="contestsToMap > 1" class="btn btn-secondary" @click="skipMapping">
          {{ t("backend.e2e.voting_round_reports.show.mapping_btn") }}
        </button>
      </div>

      <!-- Contest Results (human-readable) -->
      <ContestResult
        v-for="cr in contestResults"
        :key="`contest_result_${cr.id}`"
        :contest-result="cr"
        :voting-round-report="votingRoundReport"
        :contest="contestFor(cr.contestReference)"
        :result-orchestrators="resultOrchestrators"
      />

      <!-- Counting details: technical ceremony info per counting unit (mixing/decryption progress),
           grouped by status so a report with many orchestrators stays scannable. Open by default
           while the ceremony is still running so its live progress stays visible; collapses the
           moment the round finishes, keeping the human-readable results above it in focus. The
           user can still toggle it freely afterwards. -->
      <details class="p-3 bg-white rounded shadow-sm" :open="votingRoundReport.status !== 'finished'">
        <summary>
          {{ extractionGrouping === "ballot"
            ? t("backend.e2e.voting_round_reports.show.counting_details_by_ballot")
            : t("backend.e2e.voting_round_reports.show.counting_details_by_contest") }}
        </summary>
        <div class="vstack gap-3 mt-3">
          <details
            v-for="group in resultOrchestratorGroups"
            :key="`result_orchestrator_group_${group.status}`"
            class="result-orchestrator-group"
            :data-test="`result-orchestrator-group-${group.status}`"
            :open="isGroupOpen(group.status)"
            @toggle="onGroupToggle(group.status, $event)"
          >
            <summary class="p-3 bg-white rounded shadow-sm hstack gap-2">
              <span class="fw-bold">{{ t(`js.result_progress.states.${group.status}`) }}</span>
              <span class="badge rounded-pill text-bg-secondary">{{ group.resultOrchestrators.length }}</span>
            </summary>

            <div class="vstack gap-3 mt-3">
              <ResultOrchestrator
                v-for="resultOrchestrator in group.resultOrchestrators"
                :key="`result_orchestrator_${resultOrchestrator.id}`"
                :result-orchestrator="resultOrchestrator"
                :trustees="trustees"
                :reference="resultOrchestrator.reference"
                :threshold="orchestrator.threshold"
                :voting-round-report="votingRoundReport"
                :extraction-grouping="extractionGrouping"
                :contest="contestFor(resultOrchestrator.reference)"
                :voter-group="voterGroupFor(resultOrchestrator.reference)"
              />
            </div>
          </details>
        </div>
      </details>
    </template>
    <div v-else class="p-3 bg-white rounded shadow-sm">
      <details v-if="votingRoundReport?.status === 'failed'" class="alert alert-warning mb-0">
        <summary class="fw-bold">{{ t("js.election_client.error.headers.an_error_occurred") }}</summary>
        <div>
          <p class="mb-0">{{ t("backend.e2e.voting_round_reports.show.error_body") }}</p>
        </div>
      </details>
      <template v-else>
        <template v-if="votingRoundReport?.status === 'starting'">
          <h3>{{ t("backend.e2e.voting_round_reports.show.ceremony_starting") }}</h3>
          <div class="accent-line mt-2 mb-4"></div>
        </template>
        <div class="text-center">
          <AVSpinner size="md" style="width: 3rem; height: 3rem" color="dark" />
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, onUnmounted } from "vue";
import { CeremonyChannelSubscription } from "@/channels/CeremonyChannel";
import ResultOrchestrator from "@/components/backend/ResultOrchestrator.vue";
import ContestResult from "@/components/backend/ContestResult.vue";
import TurnoutTable from "@/components/backend/live/TurnoutTable.vue";
import apiClient from "@/entrypoints/frontend/apiClient";
import { useI18n } from "vue-i18n";
import type {
  ElectaTrustee,
  ConferenceContest,
  E2E,
  E2EVoterGroup,
  CeremonyData,
  PropType
} from "@/types";
import type { VotingRoundReportWithGrouping } from "@/types/e2e_local_extensions";

const { t } = useI18n();

const props = defineProps({
  initData: {
    type: Object as PropType<CeremonyData>,
    default: null,
  },
  orchestratorId: {
    type: Number,
    required: true,
  },
  reportId: {
    type: Number,
    required: true,
  },
  refreshOnFinish: {
    type: Boolean,
    default: true
  },
});

const subscription = ref<CeremonyChannelSubscription>(null);

const orchestrator = ref<E2E.ResultOrchestrator>(null);

const resultOrchestrators = ref<Array<E2E.ResultOrchestrator>>([]);

const contestResults = ref<Array<E2E.ContestResult>>([]);

const contests = ref<Array<ConferenceContest>>([]);

const voterGroups = ref<Array<E2EVoterGroup>>([]);

const votingRoundReports = ref<Array<E2E.VotingRoundReport>>([]);

const votingRoundReport = computed(() => votingRoundReports.value.find((report: E2E.VotingRoundReport) => report.id === props.reportId));

const extractionGrouping = computed(() => (votingRoundReport.value as VotingRoundReportWithGrouping)?.extractionGrouping);

const trustees = ref<Array<ElectaTrustee>>([]);

// The pipeline order the groups are listed in, so a group does not jump around the list when an
// orchestrator moves on to its next state. Anything unexpected sorts between the active states and
// the ones that need no attention.
const STATUS_SORT_RANK: Record<string, number> = {
  mixing: 0,
  decrypting: 1,
  decoding: 2,
  mapping: 3,
  initial: 5,
  finished: 6,
};
const UNKNOWN_STATUS_SORT_RANK = 4;

// Statuses that need no attention start collapsed; the rest start expanded.
const COLLAPSED_BY_DEFAULT_STATUSES = ["initial", "finished"];

// Only holds statuses the admin has toggled themselves - the rest fall back to the default above.
const groupOpenOverrides = ref<Record<string, boolean>>({});

const statusSortRank = (status: string) => STATUS_SORT_RANK[status] ?? UNKNOWN_STATUS_SORT_RANK;

// Keeps the order the orchestrators arrive in within each group - the report already sorts them.
const resultOrchestratorGroups = computed(() => {
  const groups: Array<{ status: string, resultOrchestrators: Array<E2E.ResultOrchestrator> }> = [];

  resultOrchestrators.value.forEach((ro: E2E.ResultOrchestrator) => {
    const group = groups.find(g => g.status === ro.status);
    if (group) {
      group.resultOrchestrators.push(ro);
    } else {
      groups.push({ status: ro.status, resultOrchestrators: [ro] });
    }
  });

  return groups.sort((a, b) => statusSortRank(a.status) - statusSortRank(b.status));
});

const isGroupOpen = (status: string) => groupOpenOverrides.value[status] ?? !COLLAPSED_BY_DEFAULT_STATUSES.includes(status);

const onGroupToggle = (status: string, event: Event) => {
  groupOpenOverrides.value[status] = (event.target as HTMLDetailsElement).open;
};

const contestsToMap = computed(() => {
  const crMapping = contestResults.value.filter((cr: any) => cr.status === "mapping").length;
  if (crMapping > 0) return crMapping;
  return resultOrchestrators.value.filter((ro: E2E.ResultOrchestrator) => ro.status === "mapping").length;
});

const handleReceived = (data: any) => {
  switch (data.type) {
    case "full_update":
      contests.value = data.state.contests
      voterGroups.value = data.state.voterGroups
      orchestrator.value = data.state.orchestrator;
      votingRoundReports.value = data.state.votingRoundReports;
      resultOrchestrators.value = votingRoundReport.value.resultOrchestrators || [];
      contestResults.value = votingRoundReport.value.contestResults || [];
      trustees.value = data.state.trustees;
      break;
    default:
      subscription.value?.fetchStatus();
  }
};

const contestFor = (contestReference: string) => contests.value.find((c: ConferenceContest) => c.reference.toLowerCase() === contestReference.toLowerCase());

const voterGroupFor = (voterGroupReference: string) => voterGroups.value.find((vg: E2EVoterGroup) => vg.reference.toLowerCase() === voterGroupReference.toLowerCase());

const skipMapping = () => {
  const postRequests = resultOrchestrators.value.map((ro: E2E.ResultOrchestrator) => {
    if (ro.status === "mapping") {
      const url = `${window.location.href.split("/voting_rounds/")[0]}/e2e/result_orchestrators/${ro.id}/create_mapping`;
      const filteredMapping: Record<string, Record<string, string>> = {};
      Object.entries(ro.writeIns).forEach(([reference]) => filteredMapping[reference] = {});
      return apiClient.post(url, { write_in_mapping: filteredMapping });
    }
  });
  Promise.all(postRequests).catch(error => console.error(error));
};

watch(votingRoundReport, (newVal, oldVal) => {
  if (props.refreshOnFinish && newVal.status === "finished" && oldVal?.status !== "finished") {
    const resultControlButtons = document.querySelector("#result-controls");
    const editVrrButton = document.querySelector("#edit-voting-round-report");
    if (resultControlButtons) {
      (resultControlButtons as HTMLElement).classList.remove("visually-hidden");
    }
    if (editVrrButton) {
      (editVrrButton as HTMLElement).classList.remove("disabled");
    }
  }
});

onMounted(() => {
  if(props.initData) {
    orchestrator.value = props.initData.orchestrator;
    votingRoundReports.value = Array.isArray(props.initData.votingRoundReports) ? props.initData.votingRoundReports : [props.initData.votingRoundReports];
    resultOrchestrators.value = votingRoundReport.value.resultOrchestrators || [];
    contestResults.value = votingRoundReport.value.contestResults || [];
    trustees.value = props.initData.trustees;
    contests.value = props.initData.contests
    voterGroups.value = props.initData.voterGroups
  }

  subscription.value = new CeremonyChannelSubscription(props.orchestratorId, {
    received: handleReceived,
  }, props.reportId);
});

onUnmounted(() => subscription.value?.unsubscribe());
</script>

<style scoped>
.result-orchestrator-group > summary {
  cursor: pointer;
}
</style>
